export type HomepageCategoryShape = 'square' | 'circle' | 'label'
export type HomepageCategoryLayout = 'grid' | 'cascade' | 'showcase'
export type CategoryTextAlignX = 'left' | 'center' | 'right'
export type CategoryTextAlignY = 'top' | 'center' | 'bottom'
export type HomepageCategoryColumns = 2 | 3 | 4 | 5 | 6

export interface HomepageCategoriesDisplay {
  shape: HomepageCategoryShape
  /** Grid classic vs asymmetric cascade mosaic */
  layout: HomepageCategoryLayout
  /** Cards per row on desktop (grid + cascade + carousel sizing) */
  columnsPerRow: HomepageCategoryColumns
  overlayEnabled: boolean
  overlayColor: string
  overlayOpacity: number
  height: number
  textAlignX: CategoryTextAlignX
  textAlignY: CategoryTextAlignY
  carouselEnabled: boolean
  carouselIntervalSec: number
}

export const DEFAULT_HOMEPAGE_CATEGORIES_DISPLAY: HomepageCategoriesDisplay = {
  shape: 'square',
  layout: 'grid',
  columnsPerRow: 4,
  overlayEnabled: true,
  overlayColor: '#000000',
  overlayOpacity: 40,
  height: 180,
  textAlignX: 'left',
  textAlignY: 'bottom',
  carouselEnabled: false,
  carouselIntervalSec: 5,
}

export const HOMEPAGE_CATEGORY_SHAPE_OPTIONS: { label: string, value: HomepageCategoryShape }[] = [
  { label: 'Pătrat (implicit)', value: 'square' },
  { label: 'Cerc', value: 'circle' },
  { label: 'Etichetă (text sub imagine)', value: 'label' },
]

export const HOMEPAGE_CATEGORY_LAYOUT_OPTIONS: { label: string, value: HomepageCategoryLayout }[] = [
  { label: 'Grilă clasică', value: 'grid' },
  { label: 'Bento (primul card mare)', value: 'cascade' },
  { label: 'Vitrină (rânduri editoriale)', value: 'showcase' },
]

export const HOMEPAGE_CATEGORY_COLUMNS_OPTIONS: { label: string, value: HomepageCategoryColumns }[] = [
  { label: '2 pe linie', value: 2 },
  { label: '3 pe linie', value: 3 },
  { label: '4 pe linie', value: 4 },
  { label: '5 pe linie', value: 5 },
  { label: '6 pe linie', value: 6 },
]

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function normalizeHexColor(raw: unknown, fallback: string): string {
  const value = String(raw ?? '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return value
  return fallback
}

function parseShape(raw: unknown): HomepageCategoryShape {
  if (raw === 'circle' || raw === 'label') return raw
  return 'square'
}

function parseLayout(raw: unknown): HomepageCategoryLayout {
  if (raw === 'cascade' || raw === 'showcase') return raw
  return 'grid'
}

function parseColumns(raw: unknown, fallback: HomepageCategoryColumns): HomepageCategoryColumns {
  const n = Number.parseInt(String(raw ?? ''), 10)
  if (n === 2 || n === 3 || n === 4 || n === 5 || n === 6) return n
  return fallback
}

export function isLabelCategoryShape(shape: HomepageCategoryShape): boolean {
  return shape === 'label'
}

export function isCircleCategoryShape(shape: HomepageCategoryShape): boolean {
  return shape === 'circle'
}

export function isCascadeCategoryLayout(layout: HomepageCategoryLayout): boolean {
  return layout === 'cascade'
}

export function isShowcaseCategoryLayout(layout: HomepageCategoryLayout): boolean {
  return layout === 'showcase'
}

/** Outer card / link classes for grid & carousel items. */
export function categoryCardClass(shape: HomepageCategoryShape): string {
  if (shape === 'circle') return 'mx-auto rounded-full'
  if (shape === 'label') return 'w-full flex flex-col gap-2.5 bg-transparent'
  return 'w-full rounded-xl'
}

/** Image frame classes (label keeps rounding only on the media). */
export function categoryImageFrameClass(shape: HomepageCategoryShape): string {
  if (shape === 'circle') return 'h-full w-full rounded-full'
  if (shape === 'label') return 'relative w-full overflow-hidden rounded-2xl bg-muted'
  return 'h-full w-full'
}

export function categoryCardStyle(shape: HomepageCategoryShape, heightPx: number): Record<string, string> {
  const height = `${heightPx}px`
  if (shape === 'circle') return { width: height, height }
  if (shape === 'label') return {}
  return { height }
}

export function categoryImageStyle(shape: HomepageCategoryShape, heightPx: number): Record<string, string> {
  if (shape === 'label') return { height: `${heightPx}px` }
  return {}
}

export function categoryCaptionAlignClass(alignX: CategoryTextAlignX): string {
  return {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[alignX]
}

/** Responsive Tailwind grid classes for homepage category rows. */
export function categoryGridClass(columns: HomepageCategoryColumns): string {
  switch (columns) {
    case 2:
      return 'grid-cols-2'
    case 3:
      return 'grid-cols-2 md:grid-cols-3'
    case 5:
      return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
    case 6:
      return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
    case 4:
    default:
      return 'grid-cols-2 md:grid-cols-4'
  }
}

/**
 * Bento mosaic: first card is a featured tile (2 cols, taller).
 * Remaining cards stay on a clean aligned grid — no zig-zag offsets.
 */
export function categoryCascadeItemClass(
  index: number,
  shape: HomepageCategoryShape,
  columns: HomepageCategoryColumns = 4,
): string {
  if (index !== 0 || shape === 'circle' || columns < 3) return ''
  return 'md:col-span-2 md:row-span-2'
}

/** Extra size for the featured bento tile. */
export function categoryCascadeCardStyle(
  shape: HomepageCategoryShape,
  heightPx: number,
  index: number,
  columns: HomepageCategoryColumns = 4,
): Record<string, string> {
  const featured = index === 0 && shape !== 'circle' && columns >= 3

  if (shape === 'label') {
    return { height: `${featured ? Math.round(heightPx * 1.85) : heightPx}px` }
  }

  if (shape === 'circle') {
    return categoryCardStyle(shape, heightPx)
  }

  if (featured) {
    return { height: `${Math.round(heightPx * 2 + 16)}px` }
  }

  return categoryCardStyle(shape, heightPx)
}

/** Flex basis for carousel cards based on columns setting. */
export function categoryCarouselWidthClass(columns: HomepageCategoryColumns): string {
  switch (columns) {
    case 2:
      return 'w-[calc(50%-0.5rem)]'
    case 3:
      return 'w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.7rem)]'
    case 5:
      return 'w-[calc(50%-0.5rem)] lg:w-[calc(20%-0.8rem)]'
    case 6:
      return 'w-[calc(50%-0.5rem)] lg:w-[calc(16.666%-0.85rem)]'
    case 4:
    default:
      return 'w-[calc(50%-0.5rem)] lg:w-[calc(25%-0.75rem)]'
  }
}

export function categoryImageSizes(columns: HomepageCategoryColumns): string {
  switch (columns) {
    case 2:
      return 'xs:50vw sm:50vw md:50vw lg:50vw'
    case 3:
      return 'xs:50vw sm:50vw md:33vw lg:33vw'
    case 5:
      return 'xs:50vw sm:50vw md:33vw lg:20vw'
    case 6:
      return 'xs:50vw sm:50vw md:33vw lg:16vw'
    case 4:
    default:
      return 'xs:50vw sm:50vw md:25vw lg:25vw'
  }
}

export function parseHomepageCategoriesDisplay(
  raw?: string | null,
  fallback: HomepageCategoriesDisplay = DEFAULT_HOMEPAGE_CATEGORIES_DISPLAY,
): HomepageCategoriesDisplay {
  if (!raw) return { ...fallback }

  try {
    const parsed = JSON.parse(raw) as Partial<HomepageCategoriesDisplay>
    if (!parsed || typeof parsed !== 'object') return { ...fallback }

    const shape = parseShape(parsed.shape)
    const layout = parseLayout(parsed.layout)
    const columnsPerRow = parseColumns(parsed.columnsPerRow, fallback.columnsPerRow)
    const overlayEnabled = parsed.overlayEnabled !== false
    const overlayColor = normalizeHexColor(parsed.overlayColor, fallback.overlayColor)
    const overlayOpacity = clamp(
      Number.parseInt(String(parsed.overlayOpacity ?? ''), 10) || fallback.overlayOpacity,
      0,
      100,
    )
    const height = clamp(
      Number.parseInt(String(parsed.height ?? ''), 10) || fallback.height,
      80,
      400,
    )
    const textAlignX = parsed.textAlignX === 'center' || parsed.textAlignX === 'right'
      ? parsed.textAlignX
      : 'left'
    const textAlignY = parsed.textAlignY === 'top' || parsed.textAlignY === 'center'
      ? parsed.textAlignY
      : 'bottom'
    const carouselEnabled = parsed.carouselEnabled === true
    const carouselIntervalSec = clamp(
      Number.parseInt(String(parsed.carouselIntervalSec ?? ''), 10) || fallback.carouselIntervalSec,
      2,
      30,
    )

    return {
      shape,
      layout,
      columnsPerRow,
      overlayEnabled,
      overlayColor,
      overlayOpacity,
      height,
      textAlignX,
      textAlignY,
      carouselEnabled,
      carouselIntervalSec,
    }
  } catch {
    return { ...fallback }
  }
}

export function hexToRgba(hex: string, opacityPercent: number): string {
  const normalized = normalizeHexColor(hex, '#000000').slice(1)
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  const alpha = clamp(opacityPercent, 0, 100) / 100
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function categoryTextPositionClasses(
  alignX: CategoryTextAlignX,
  alignY: CategoryTextAlignY,
): string {
  const justify = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[alignX]
  const items = {
    top: 'items-start',
    center: 'items-center',
    bottom: 'items-end',
  }[alignY]

  return `flex ${items} ${justify}`
}
